import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, X, ShieldCheck } from "lucide-react";

// The flat fee is intentionally in one place so it's easy to change later.
const FEE_USD = 299;

export default function BrandPricing() {
  const buys = [
    "One product listed in the Try & Critique pool.",
    "Fair distribution to real users who ask to try it.",
    "Honest Field Notes from anyone the site ships the product to.",
    "A permanent product page you can link to.",
  ];
  const doesNot = [
    "Any placement in The Grove, search, or category feeds.",
    "Any ranking, boost, or visibility beyond this section.",
    "Any preview, approval, or edit power over reviews.",
    "Any ability to remove or bury negative reviews.",
    "A guaranteed positive outcome — critical Field Notes stay.",
  ];

  return (
    <main className="max-w-4xl mx-auto px-6 pt-12 pb-24">
      <span className="accent-label text-[#B79256]">brand pricing · try &amp; critique</span>
      <h1 className="font-serif-display text-5xl sm:text-6xl text-[#2A2823] mt-3 leading-none">
        One flat fee.<br/>
        <em className="not-italic text-[#4E5A3D]">Nothing else for sale.</em>
      </h1>
      <p className="mt-6 max-w-2xl text-lg text-[#2A2823]/75 leading-relaxed">
        Willoah doesn't sell placement, boosted posts, or positive coverage. What a brand can buy is a fair shot at being tested by real users — and the honest reviews that follow.
      </p>

      {/* The number */}
      <section className="mt-12 rounded-3xl border border-[#4E5A3D]/25 bg-[#4E5A3D]/6 p-8 sm:p-10">
        <div className="accent-label text-[#4E5A3D]">flat fee</div>
        <div className="mt-2 flex items-baseline gap-3">
          <span data-testid="fee-amount" className="font-serif-display text-6xl sm:text-7xl text-[#2A2823] leading-none">
            ${FEE_USD}
          </span>
          <span className="text-[#2A2823]/60 text-sm">USD · per product · no recurring charges</span>
        </div>
        <p className="mt-5 max-w-xl text-[#2A2823]/75 leading-relaxed">
          Paid one time to add your product to the Try &amp; Critique pool. The fee buys the opportunity to be reviewed — full stop. It doesn't buy visibility, ranking, or a guarantee that anyone will love it.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Button asChild className="rounded-full h-12 px-7 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none">
            <Link data-testid="pricing-apply-btn" to="/apply/form">Apply to be reviewed</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-12 px-7 border-[#B79256]/50 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823]">
            <Link to="/try-and-critique">See what's in the pool</Link>
          </Button>
        </div>
      </section>

      {/* What it buys vs. doesn't */}
      <section className="mt-12 grid md:grid-cols-2 gap-6">
        <div data-testid="fee-includes" className="rounded-2xl border border-[#4E5A3D]/25 bg-white p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.5}/>
            <div className="accent-label text-[#4E5A3D]">what the fee buys</div>
          </div>
          <ul className="mt-4 space-y-3">
            {buys.map((b) => (
              <li key={b} className="flex gap-3 text-[#2A2823]/85">
                <Check className="w-4 h-4 text-[#4E5A3D] flex-shrink-0 mt-1" strokeWidth={2}/>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div data-testid="fee-excludes" className="rounded-2xl border border-[#B79256]/30 bg-white p-6">
          <div className="flex items-center gap-2">
            <X className="w-5 h-5 text-[#B79256]" strokeWidth={1.75}/>
            <div className="accent-label text-[#B79256]">what it does not buy</div>
          </div>
          <ul className="mt-4 space-y-3">
            {doesNot.map((b) => (
              <li key={b} className="flex gap-3 text-[#2A2823]/85">
                <X className="w-4 h-4 text-[#B79256] flex-shrink-0 mt-1" strokeWidth={2}/>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How the flow actually works */}
      <section className="mt-14">
        <h2 className="font-serif-display text-3xl text-[#2A2823]">How it works</h2>
        <ol className="mt-6 space-y-6">
          {[
            ["Apply", "Fill out the short form with your product, photo, and category. The site owner reviews personally."],
            ["Approved & paid", "If approved, you settle the flat fee out-of-band and the product goes live in the Try & Critique pool."],
            ["Users ask to try", "Real Willoah users request the product. The site owner picks a fair set of recipients."],
            ["Honest reviews", `Recipients plant a branch labeled "Gifted to me" — the label is locked so the disclosure is automatic and public.`],
            ["That's it", "Nothing about the review is negotiable, editable, or removable by you. Critical reviews stay. Willoah moderates only for abuse."],
          ].map(([title, body], i) => (
            <li key={title} className="flex gap-5">
              <span className="font-serif-display text-3xl text-[#B79256] w-10 flex-shrink-0 leading-none">{i + 1}</span>
              <div>
                <div className="font-serif-display text-xl text-[#2A2823]">{title}</div>
                <p className="text-[#2A2823]/70 mt-1 leading-relaxed">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Rules stated plainly */}
      <section className="mt-14 rounded-3xl border border-[#B79256]/30 bg-white p-6 sm:p-8">
        <div className="accent-label text-[#B79256]">plain-language rules</div>
        <p className="font-serif-display text-2xl text-[#2A2823] mt-2 leading-snug">
          Nothing here is negotiable, and applying means agreeing to all of it.
        </p>
        <ul className="mt-5 space-y-2 text-[#2A2823]/85 text-sm leading-relaxed">
          <li>· Reviews go live without brand preview or approval.</li>
          <li>· Willoah will never remove a merely-negative review on a brand's behalf.</li>
          <li>· Try &amp; Critique reviews are treated identically to organic branches in every ranking or trust signal.</li>
          <li>· A brand that pressures a reviewer forfeits its listing and its fee.</li>
          <li>· No hidden fees, no upgrades. This is the only paid product on Willoah for brands. Any sponsored content elsewhere is always clearly labeled.</li>
        </ul>
      </section>

      <div className="mt-14 text-center">
        <Button asChild className="rounded-full h-12 px-8 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none text-base">
          <Link data-testid="pricing-apply-btn-bottom" to="/apply/form">Continue to application →</Link>
        </Button>
        <p className="text-xs text-[#2A2823]/50 mt-3">Applying is free. The flat fee is only settled after the site owner approves the product.</p>
      </div>
    </main>
  );
}
